import {
  AccountInfo,
  createStandardPublicClientApplication,
  InteractionRequiredAuthError,
  type RedirectRequest,
  SilentRequest,
} from '@azure/msal-browser';

const scopes = ['openid', 'profile', 'email', `api://${AZURE_CLIENT_ID}/User`];

export const loginRequest: RedirectRequest = {
  scopes: scopes,
  domainHint: 'dnb.no',
};

export const msalInstance = await createStandardPublicClientApplication({
  auth: {
    clientId: AZURE_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${AZURE_TENANT}`,
    redirectUri: AZURE_REDIRECT_URI,
    postLogoutRedirectUri: '/',
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: 'localStorage',
  },
});

export async function signInWithAzure(): Promise<void> {
  return msalInstance.loginRedirect(loginRequest);
}

export async function logoutWithAzure(): Promise<void> {
  return msalInstance.logoutRedirect();
}

/**
 * Returns the request payload required by acquireTokenSilent
 * @param account the account to request a token for
 */
function getSilentRequest(account: AccountInfo): SilentRequest {
  return {
    scopes: scopes,
    account: account,
  };
}

export async function acquireTokenSilentWithAzure(forceRefresh: boolean = false): Promise<void> {
  const [account] = msalInstance.getAllAccounts();

  if (!account) return signInWithAzure();

  try {
    await msalInstance.acquireTokenSilent({
      ...getSilentRequest(account),
      forceRefresh,
    });
  } catch (error) {
    // Catch interaction_required errors and call interactive method to resolve
    if (error instanceof InteractionRequiredAuthError) {
      try {
        await msalInstance.acquireTokenRedirect({
          ...loginRequest,
          account,
        });
      } catch (error_) {
        // eslint-disable-next-line no-console
        console.log(error_);
      }
    } else {
      localStorage.clear();
      location.reload();
    }
  }
}

/**
 * Call {@link isSignedInWithAzure} before calling this method.
 */
export async function getAzureToken(): Promise<string> {
  const [account] = msalInstance.getAllAccounts();

  const tokenRequest = getSilentRequest(account!);

  const response = await msalInstance.acquireTokenSilent(tokenRequest);
  return response.accessToken;
}

/**
 * 1. Check whether the cache contains any accounts.
 * 2. Check whether the account in the cache is still valid for fetching a token. This check is required as the
 *  account might be present in the cache, but the refresh-token could have expired.
 * 3. If the refresh token has expired, try to re-authenticate with sso silent login.
 */
export async function isSignedInWithAzure(): Promise<boolean> {
  const [account] = msalInstance.getAllAccounts();
  if (!account) return false;

  const tokenRequest = getSilentRequest(account);
  try {
    await msalInstance.acquireTokenSilent(tokenRequest);
    return true;
  } catch {
    try {
      await msalInstance.ssoSilent(tokenRequest);
      return true;
    } catch {
      return false;
    }
  }
}
